import { CampaignChannel } from '../types/campaigns'
import { STARTER_ES } from './templateStartersEs'

/** Contenido de la plantilla. Viaja al backend tal cual: no es i18n de interfaz. */
export interface TemplateStarterContent {
  subject?: string
  bodyHtml?: string
  bodyText: string
  variables: string[]
}

/** Variante de un starter en un idioma concreto (ver `templateStartersEs.ts`). */
export interface TemplateStarterLocalized {
  name: string
  description: string
  content: TemplateStarterContent
}

export interface TemplateStarter {
  id: string
  name: string
  description: string
  icon: string
  channel: CampaignChannel
  preview: string
  content: TemplateStarterContent
}

/** Idioma del contenido que se elige en el selector de plantillas iniciales. */
export type StarterLocale = 'en' | 'es'

/** `true` si el starter existe en ese idioma; el inglés es el original. */
export const starterHasLocale = (starter: TemplateStarter, locale: StarterLocale) =>
  locale === 'en' || Boolean(STARTER_ES[starter.id])

/**
 * Devuelve el starter con nombre, descripción y contenido en el idioma pedido.
 * Conserva la forma de `TemplateStarter`, así que los consumidores
 * (`TemplateFormModal`, `AutomationWizard`) no necesitan cambios.
 */
export const resolveStarter = (
  starter: TemplateStarter,
  locale: StarterLocale
): TemplateStarter => {
  const localized = locale === 'es' ? STARTER_ES[starter.id] : undefined
  if (!localized) return starter
  return {
    ...starter,
    name: localized.name,
    description: localized.description,
    content: localized.content,
  }
}

// ─── Email Templates ──────────────────────────────────────────────────────────

export const EMAIL_STARTERS: TemplateStarter[] = [
  {
    id: 'newsletter',
    name: 'Newsletter',
    description: 'Regular updates, company news, and blog posts',
    icon: '📧',
    channel: 'EMAIL' as CampaignChannel,
    preview: '',
    content: {
      subject: 'Your {{agency_name}} Newsletter - {{month}} Edition',
      variables: ['first_name', 'agency_name', 'month', 'unsubscribe_url'],
      bodyText: `Hi {{first_name}},\n\nWelcome to our latest newsletter!\n\n[Main content here]\n\nBest regards,\nThe {{agency_name}} Team`,
      bodyHtml: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif;background:#f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:40px 0;">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.1);">
      <tr><td align="center" style="background:#ffffff;padding:24px 24px 4px;">
        <img src="cid:logo_cid" alt="{{agency_name}}" style="height:44px;max-width:240px;display:block;margin:0 auto;border:0;" />
      </td></tr>
      <tr><td align="center" style="background:linear-gradient(135deg,#667eea,#764ba2);padding:40px 20px;">
        <h1 style="color:#fff;margin:0;font-size:28px;">{{agency_name}}</h1>
        <p style="color:#fff;margin:10px 0 0;font-size:14px;opacity:.9;">Monthly Newsletter</p>
      </td></tr>
      <tr><td style="padding:40px 30px;">
        <p style="color:#333;font-size:16px;line-height:1.6;margin:0 0 20px;">Hi <strong>{{first_name}}</strong>,</p>
        <p style="color:#666;font-size:16px;line-height:1.6;margin:0 0 30px;">Welcome to our latest newsletter! We're excited to share what's new with you this month.</p>
        <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:30px;"><tr>
          <td style="background:#f8f9fa;border-radius:8px;padding:30px;">
            <h2 style="color:#333;font-size:22px;margin:0 0 15px;">Featured Update</h2>
            <p style="color:#666;font-size:15px;line-height:1.6;margin:0;">Add your main content here.</p>
          </td>
        </tr></table>
        <h3 style="color:#333;font-size:18px;margin:0 0 15px;">What's New</h3>
        <ul style="color:#666;font-size:15px;line-height:1.8;margin:0 0 30px;padding-left:20px;">
          <li>Update item 1</li><li>Update item 2</li><li>Update item 3</li>
        </ul>
        <p style="color:#666;font-size:16px;line-height:1.6;margin:0;">Best regards,<br><strong>The {{agency_name}} Team</strong></p>
      </td></tr>
      <tr><td style="background:#f8f9fa;padding:30px;text-align:center;border-top:1px solid #e9ecef;">
        <p style="color:#999;font-size:12px;margin:0 0 10px;">© {{agency_name}}. All rights reserved.</p>
        <p style="color:#999;font-size:12px;margin:0;"><a href="{{unsubscribe_url}}" style="color:#667eea;text-decoration:none;">Unsubscribe</a></p>
      </td></tr>
    </table>
  </td></tr></table>
</body>
</html>`,
    },
  },
  {
    id: 'welcome-insurance',
    name: 'Welcome New Client',
    description: 'Warm welcome email when a new policy is created or completed',
    icon: '🤝',
    channel: 'EMAIL' as CampaignChannel,
    preview: '',
    content: {
      subject: 'Welcome to {{agency_name}}, {{first_name}}!',
      variables: ['first_name', 'agency_name', 'agency_phone', 'client_center_url', 'unsubscribe_url'],
      bodyText: `Hi {{first_name}},\n\nThank you for trusting us with your insurance needs. We're thrilled to have you as a client.\n\nIf you have any questions, don't hesitate to reach out.\n\n{{agency_name}}\n{{agency_phone}}`,
      bodyHtml: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif;background:#f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:40px 0;">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.1);">
      <tr><td align="center" style="background:#ffffff;padding:24px 24px 4px;">
        <img src="cid:logo_cid" alt="{{agency_name}}" style="height:44px;max-width:240px;display:block;margin:0 auto;border:0;" />
      </td></tr>
      <tr><td align="center" style="background:#1a5276;padding:40px 20px;">
        <h1 style="color:#fff;margin:0;font-size:28px;">Welcome to {{agency_name}}!</h1>
        <p style="color:#fff;margin:10px 0 0;font-size:15px;opacity:.9;">We're glad you're here</p>
      </td></tr>
      <tr><td style="padding:40px 30px;">
        <p style="color:#333;font-size:16px;line-height:1.6;margin:0 0 20px;">Hi <strong>{{first_name}}</strong>,</p>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 20px;">
          Thank you for trusting us with your insurance needs. We're committed to making sure you have the right coverage for you and your family.
        </p>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 32px;">
          If you ever have questions or need to make changes to your policy, we're just a call or message away.
        </p>
        <table cellpadding="0" cellspacing="0"><tr>
          <td style="background:#1a5276;border-radius:6px;">
            <a href="{{client_center_url}}" style="display:inline-block;padding:14px 32px;color:#fff;font-size:15px;font-weight:bold;text-decoration:none;">Access Your Client Center</a>
          </td>
        </tr></table>
      </td></tr>
      <tr><td style="background:#f8f9fa;padding:24px 30px;text-align:center;border-top:1px solid #e9ecef;">
        <p style="color:#999;font-size:13px;margin:0;">{{agency_name}} · {{agency_phone}}</p>
        <p style="color:#999;font-size:12px;margin:8px 0 0;"><a href="{{unsubscribe_url}}" style="color:#aaa;text-decoration:none;">Unsubscribe</a></p>
      </td></tr>
    </table>
  </td></tr></table>
</body>
</html>`,
    },
  },
  {
    id: 'policy-renewal',
    name: 'Policy Renewal Reminder',
    description: 'Alert clients that their policy is coming up for renewal',
    icon: '🔄',
    channel: 'EMAIL' as CampaignChannel,
    preview: '',
    content: {
      subject: 'Your Policy Renews Soon, {{first_name}} — Action Required',
      variables: ['first_name', 'agency_name', 'agency_phone', 'renewal_date', 'policy_type', 'unsubscribe_url'],
      bodyText: `Hi {{first_name}},\n\nYour {{policy_type}} policy is coming up for renewal on {{renewal_date}}.\n\nPlease contact us to review your coverage and ensure you stay protected.\n\n{{agency_name}}\n{{agency_phone}}`,
      bodyHtml: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif;background:#f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:40px 0;">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.1);">
      <tr><td align="center" style="background:#ffffff;padding:24px 24px 4px;">
        <img src="cid:logo_cid" alt="{{agency_name}}" style="height:44px;max-width:240px;display:block;margin:0 auto;border:0;" />
      </td></tr>
      <tr><td align="center" style="background:#154360;padding:40px 20px;">
        <h1 style="color:#fff;margin:0;font-size:26px;">Policy Renewal Notice</h1>
        <p style="color:#aed6f1;margin:10px 0 0;font-size:15px;">Action required before {{renewal_date}}</p>
      </td></tr>
      <tr><td style="padding:40px 30px;">
        <p style="color:#333;font-size:16px;line-height:1.6;margin:0 0 20px;">Hi <strong>{{first_name}}</strong>,</p>
        <div style="background:#eaf2ff;border-left:4px solid #2980b9;padding:20px;border-radius:4px;margin-bottom:24px;">
          <p style="color:#1a5276;font-size:15px;line-height:1.6;margin:0;">
            Your <strong>{{policy_type}}</strong> policy is scheduled to renew on <strong>{{renewal_date}}</strong>.
          </p>
        </div>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 20px;">
          We'd love to review your coverage with you to make sure everything is still the right fit. Sometimes your needs change and there may be better options available.
        </p>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 32px;">
          Contact us before your renewal date to go over your options.
        </p>
        <table cellpadding="0" cellspacing="0"><tr>
          <td style="background:#154360;border-radius:6px;">
            <a href="tel:{{agency_phone}}" style="display:inline-block;padding:14px 32px;color:#fff;font-size:15px;font-weight:bold;text-decoration:none;">Call Us Now</a>
          </td>
        </tr></table>
      </td></tr>
      <tr><td style="background:#f8f9fa;padding:24px 30px;text-align:center;border-top:1px solid #e9ecef;">
        <p style="color:#999;font-size:13px;margin:0;">{{agency_name}} · {{agency_phone}}</p>
        <p style="color:#999;font-size:12px;margin:8px 0 0;"><a href="{{unsubscribe_url}}" style="color:#aaa;text-decoration:none;">Unsubscribe</a></p>
      </td></tr>
    </table>
  </td></tr></table>
</body>
</html>`,
    },
  },
  {
    id: 'open-enrollment',
    name: 'Open Enrollment Reminder',
    description: 'Announce the U.S. Open Enrollment season and offer help with the renewal',
    icon: '🗓️',
    channel: 'EMAIL' as CampaignChannel,
    preview: '',
    content: {
      subject: 'Open Enrollment is coming, {{first_name}} — {{agency_name}}',
      variables: ['first_name', 'agency_name', 'agency_phone', 'unsubscribe_url'],
      bodyText: `Hello {{first_name}},\n\nWe are reaching out from {{agency_name}} to remind you that the U.S. insurance renewal season (Open Enrollment) is approaching.\n\nWe want to make sure you are ready to review your policy and keep your coverage protected for the coming year. We will contact you shortly to guide you through the process. If you would like to get ahead and talk to us now, call us at {{agency_phone}}.\n\nThank you for trusting us!\n\n{{agency_name}}`,
      bodyHtml: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif;background:#f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:40px 0;">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.1);">
      <tr><td align="center" style="background:#ffffff;padding:24px 24px 4px;">
        <img src="cid:logo_cid" alt="{{agency_name}}" style="height:44px;max-width:240px;display:block;margin:0 auto;border:0;" />
      </td></tr>
      <tr><td align="center" style="background:#1b4f72;padding:36px 20px;">
        <div style="font-size:44px;margin-bottom:10px;">🗓️</div>
        <h1 style="color:#fff;margin:0;font-size:26px;">Open Enrollment</h1>
        <p style="color:#aed6f1;margin:10px 0 0;font-size:15px;">Renewal season in the U.S. is coming</p>
      </td></tr>
      <tr><td style="padding:40px 30px;">
        <p style="color:#333;font-size:16px;line-height:1.6;margin:0 0 20px;">Hello <strong>{{first_name}}</strong>,</p>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 20px;">
          We are reaching out from <strong>{{agency_name}}</strong> to remind you that the U.S. insurance renewal season (<strong>Open Enrollment</strong>) is approaching.
        </p>
        <div style="background:#eaf2ff;border-left:4px solid #2980b9;padding:20px;border-radius:4px;margin-bottom:24px;">
          <p style="color:#1a5276;font-size:15px;line-height:1.6;margin:0;">
            We want to make sure you are ready to review your policy and keep your coverage protected for the coming year.
          </p>
        </div>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 20px;">
          We will contact you shortly to give you the best guidance through this process. If you would like to get ahead and talk to us now, call us at <strong>{{agency_phone}}</strong>.
        </p>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 32px;">Thank you for trusting us!</p>
        <table cellpadding="0" cellspacing="0"><tr>
          <td style="background:#1b4f72;border-radius:6px;">
            <a href="tel:{{agency_phone}}" style="display:inline-block;padding:14px 32px;color:#fff;font-size:15px;font-weight:bold;text-decoration:none;">Call us now</a>
          </td>
        </tr></table>
      </td></tr>
      <tr><td style="background:#f8f9fa;padding:24px 30px;text-align:center;border-top:1px solid #e9ecef;">
        <p style="color:#999;font-size:13px;margin:0;">{{agency_name}} · {{agency_phone}}</p>
        <p style="color:#999;font-size:12px;margin:8px 0 0;"><a href="{{unsubscribe_url}}" style="color:#aaa;text-decoration:none;">Unsubscribe</a></p>
      </td></tr>
    </table>
  </td></tr></table>
</body>
</html>`,
    },
  },
  {
    id: 'cross-sell-home',
    name: 'Cross-sell: Add Home Insurance',
    description: 'Offer home coverage to clients who already have auto insurance',
    icon: '🏠',
    channel: 'EMAIL' as CampaignChannel,
    preview: '',
    content: {
      subject: 'Protect your home too, {{first_name}} — bundle & save up to 20%',
      variables: ['first_name', 'agency_name', 'agency_phone', 'agency_url', 'unsubscribe_url'],
      bodyText: `Hi {{first_name}},\n\nYou're already covered with auto insurance — great choice!\n\nDid you know bundling home insurance with us can save you up to 20% on both policies?\n\nLet us put together a quick quote. No obligation.\n\n{{agency_name}}\n{{agency_phone}}`,
      bodyHtml: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif;background:#f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:40px 0;">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.1);">
      <tr><td align="center" style="background:#ffffff;padding:24px 24px 4px;">
        <img src="cid:logo_cid" alt="{{agency_name}}" style="height:44px;max-width:240px;display:block;margin:0 auto;border:0;" />
      </td></tr>
      <tr><td align="center" style="background:#1a6b3c;padding:40px 20px;">
        <div style="font-size:48px;margin-bottom:12px;">🏠</div>
        <h1 style="color:#fff;margin:0;font-size:26px;">Protect Your Home Too</h1>
      </td></tr>
      <tr><td style="padding:40px 30px;">
        <p style="color:#333;font-size:16px;line-height:1.6;margin:0 0 20px;">Hi <strong>{{first_name}}</strong>,</p>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 20px;">
          You're already covered with your auto policy — great choice! Did you know bundling your <strong>home insurance</strong> with us can save you up to <strong>20%</strong> on both policies?
        </p>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 32px;">
          Let us put together a quick quote for you. It only takes a few minutes and there's no obligation.
        </p>
        <table cellpadding="0" cellspacing="0"><tr>
          <td style="background:#1a6b3c;border-radius:6px;">
            <a href="{{agency_url}}" style="display:inline-block;padding:14px 32px;color:#fff;font-size:15px;font-weight:bold;text-decoration:none;">Get My Home Quote</a>
          </td>
        </tr></table>
      </td></tr>
      <tr><td style="background:#f8f9fa;padding:24px 30px;text-align:center;border-top:1px solid #e9ecef;">
        <p style="color:#999;font-size:13px;margin:0;">{{agency_name}} · {{agency_phone}}</p>
        <p style="color:#999;font-size:12px;margin:8px 0 0;"><a href="{{unsubscribe_url}}" style="color:#aaa;text-decoration:none;">Unsubscribe</a></p>
      </td></tr>
    </table>
  </td></tr></table>
</body>
</html>`,
    },
  },
  {
    id: 'cancellation-notice',
    name: 'Policy Cancellation Notice',
    description: 'Reach out when a policy status changes to prevent coverage gaps',
    icon: '⚠️',
    channel: 'EMAIL' as CampaignChannel,
    preview: '',
    content: {
      subject: '⚠️ Important: Policy Notice for {{first_name}}',
      variables: ['first_name', 'agency_name', 'agency_phone', 'agency_url', 'unsubscribe_url'],
      bodyText: `Hi {{first_name}},\n\nWe've noticed a change in your policy status. Please contact us as soon as possible so we can review your options and keep your coverage active.\n\n{{agency_name}}\n{{agency_phone}}`,
      bodyHtml: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif;background:#f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:40px 0;">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.1);">
      <tr><td align="center" style="background:#ffffff;padding:24px 24px 4px;">
        <img src="cid:logo_cid" alt="{{agency_name}}" style="height:44px;max-width:240px;display:block;margin:0 auto;border:0;" />
      </td></tr>
      <tr><td align="center" style="background:#922b21;padding:40px 20px;">
        <div style="font-size:48px;margin-bottom:12px;">⚠️</div>
        <h1 style="color:#fff;margin:0;font-size:26px;">Important Policy Notice</h1>
      </td></tr>
      <tr><td style="padding:40px 30px;">
        <p style="color:#333;font-size:16px;line-height:1.6;margin:0 0 20px;">Hi <strong>{{first_name}}</strong>,</p>
        <div style="background:#fdf2f2;border-left:4px solid #c0392b;padding:20px;border-radius:4px;margin-bottom:24px;">
          <p style="color:#7b241c;font-size:15px;line-height:1.6;margin:0;">
            We've noticed a change in the status of your policy. Gaps in coverage can leave you exposed to significant financial risk.
          </p>
        </div>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 32px;">
          Please contact us as soon as possible so we can review your options and find the best solution to keep your coverage active.
        </p>
        <table cellpadding="0" cellspacing="0"><tr>
          <td style="background:#922b21;border-radius:6px;">
            <a href="{{agency_url}}" style="display:inline-block;padding:14px 32px;color:#fff;font-size:15px;font-weight:bold;text-decoration:none;">Contact Us Now</a>
          </td>
        </tr></table>
      </td></tr>
      <tr><td style="background:#f8f9fa;padding:24px 30px;text-align:center;border-top:1px solid #e9ecef;">
        <p style="color:#999;font-size:13px;margin:0;">{{agency_name}} · {{agency_phone}}</p>
        <p style="color:#999;font-size:12px;margin:8px 0 0;"><a href="{{unsubscribe_url}}" style="color:#aaa;text-decoration:none;">Unsubscribe</a></p>
      </td></tr>
    </table>
  </td></tr></table>
</body>
</html>`,
    },
  },
  {
    id: 'informational',
    name: 'Informational',
    description: 'Clean layout for announcements, updates, and educational content',
    icon: '📰',
    channel: 'EMAIL' as CampaignChannel,
    preview: '',
    content: {
      subject: 'Important Update: {{update_title}}',
      variables: ['first_name', 'update_title', 'agency_name', 'unsubscribe_url'],
      bodyText: `Hi {{first_name}},\n\nWe wanted to inform you about: {{update_title}}\n\n[Details here]\n\nBest regards,\n{{agency_name}}`,
      bodyHtml: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif;background:#f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:40px 0;">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.1);">
      <tr><td align="center" style="background:#ffffff;padding:24px 24px 4px;">
        <img src="cid:logo_cid" alt="{{agency_name}}" style="height:44px;max-width:240px;display:block;margin:0 auto;border:0;" />
      </td></tr>
      <tr><td style="background:#2c3e50;padding:30px;border-bottom:4px solid #3498db;">
        <h1 style="color:#fff;margin:0;font-size:24px;font-weight:600;">{{agency_name}}</h1>
        <p style="color:#ecf0f1;margin:8px 0 0;font-size:14px;">Important Update</p>
      </td></tr>
      <tr><td style="padding:40px 30px;">
        <p style="color:#333;font-size:16px;margin:0 0 20px;">Hi <strong>{{first_name}}</strong>,</p>
        <h2 style="color:#2c3e50;font-size:24px;margin:0 0 20px;font-weight:600;border-left:4px solid #3498db;padding-left:15px;">{{update_title}}</h2>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 25px;">We wanted to inform you about an important update. Please read the following information carefully.</p>
        <div style="background:#f8f9fa;border-left:4px solid #3498db;padding:20px;margin:0 0 25px;border-radius:4px;">
          <h3 style="color:#2c3e50;font-size:18px;margin:0 0 15px;font-weight:600;">Key Points:</h3>
          <ul style="color:#555;font-size:15px;line-height:1.8;margin:0;padding-left:20px;">
            <li>Important point one</li><li>Important point two</li><li>Important point three</li>
          </ul>
        </div>
        <table cellpadding="0" cellspacing="0" align="center" style="margin:0 auto 25px;"><tr>
          <td style="background:#3498db;border-radius:4px;padding:15px 40px;">
            <a href="#" style="color:#fff;text-decoration:none;font-size:16px;font-weight:600;display:block;">Learn More</a>
          </td>
        </tr></table>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0;">Best regards,<br><strong>{{agency_name}} Team</strong></p>
      </td></tr>
      <tr><td style="background:#f8f9fa;padding:25px 30px;text-align:center;border-top:1px solid #e9ecef;">
        <p style="color:#999;font-size:12px;margin:0 0 10px;">© {{agency_name}}. All rights reserved.</p>
        <p style="color:#999;font-size:12px;margin:0;"><a href="{{unsubscribe_url}}" style="color:#3498db;text-decoration:none;">Unsubscribe</a></p>
      </td></tr>
    </table>
  </td></tr></table>
</body>
</html>`,
    },
  },
]

// ─── SMS Templates ────────────────────────────────────────────────────────────

export const SMS_STARTERS: TemplateStarter[] = [
  {
    id: 'sms-birthday',
    name: 'Birthday Greeting',
    description: 'Send a warm birthday message to your clients',
    icon: '🎂',
    channel: 'SMS' as CampaignChannel,
    preview: '',
    content: {
      variables: ['first_name', 'agency_name'],
      bodyText: '🎂 Happy Birthday, {{first_name}}! Wishing you a wonderful day filled with joy. From everyone at {{agency_name}}. Reply STOP to unsubscribe.',
    },
  },
  {
    id: 'sms-policy-reminder',
    name: 'Policy Renewal Reminder',
    description: 'Quick SMS reminder that a policy renewal is coming up',
    icon: '🔄',
    channel: 'SMS' as CampaignChannel,
    preview: '',
    content: {
      variables: ['first_name', 'policy_type', 'renewal_date', 'agency_name', 'agency_phone'],
      bodyText: 'Hi {{first_name}}, your {{policy_type}} policy renews on {{renewal_date}}. Call us at {{agency_phone}} to review your coverage. {{agency_name}}. Reply STOP to opt out.',
    },
  },
  {
    id: 'sms-followup',
    name: 'Lead Follow-up',
    description: 'Re-engage a lead that hasn\'t responded recently',
    icon: '👋',
    channel: 'SMS' as CampaignChannel,
    preview: '',
    content: {
      variables: ['first_name', 'agency_name', 'agency_phone'],
      bodyText: 'Hi {{first_name}}, just checking in! We\'d love to help you find the right insurance coverage. Call or reply anytime — no pressure. {{agency_name}} {{agency_phone}}. Reply STOP to unsubscribe.',
    },
  },
  {
    id: 'sms-cross-sell',
    name: 'Cross-sell Opportunity',
    description: 'Offer an additional line of coverage via text',
    icon: '💡',
    channel: 'SMS' as CampaignChannel,
    preview: '',
    content: {
      variables: ['first_name', 'coverage_type', 'agency_name', 'agency_phone'],
      bodyText: 'Hi {{first_name}}, did you know you could save by bundling {{coverage_type}} with your current policy? Let\'s talk — call {{agency_phone}}. {{agency_name}}. Reply STOP to opt out.',
    },
  },
  {
    id: 'sms-cancellation',
    name: 'Cancellation Alert',
    description: 'Urgent outreach when a policy status changes',
    icon: '⚠️',
    channel: 'SMS' as CampaignChannel,
    preview: '',
    content: {
      variables: ['first_name', 'agency_name', 'agency_phone'],
      bodyText: 'Hi {{first_name}}, we noticed a change in your policy status. Please call us at {{agency_phone}} ASAP so we can keep you covered. {{agency_name}}. Reply STOP to opt out.',
    },
  },
  {
    id: 'sms-welcome',
    name: 'Welcome New Client',
    description: 'Quick welcome text after a new policy is created',
    icon: '🤝',
    channel: 'SMS' as CampaignChannel,
    preview: '',
    content: {
      variables: ['first_name', 'agency_name', 'agency_phone'],
      bodyText: 'Welcome, {{first_name}}! 🎉 We\'re glad to have you as a client at {{agency_name}}. Questions? Call or text {{agency_phone}} anytime. Reply STOP to unsubscribe.',
    },
  },
]

// ─── Combined export (backwards compatible) ───────────────────────────────────

export const TEMPLATE_STARTERS: TemplateStarter[] = [
  ...EMAIL_STARTERS,
  ...SMS_STARTERS,
]
